import { Badge, Button, Card, Group, Progress, Skeleton, Stack, Text, Title } from '@mantine/core'
import { IconPlayerPlay } from '@tabler/icons-react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useSimilarTracks, useTrack, useTrackLyrics } from '~/api/queries'
import { CoverArt } from '~/components/blocks/CoverArt/CoverArt'
import { EnqueueButton } from '~/components/blocks/EnqueueButton'
import { FavoriteButton } from '~/components/blocks/FavoriteButton/FavoriteButton'
import { SyncedLyrics } from '~/components/blocks/SyncedLyrics/SyncedLyrics'
import { TrackTable } from '~/components/blocks/TrackTable/TrackTable'
import { trackToQueueItem } from '~/components/player/utils'
import { useCurrentTime } from '~/hooks/use-current-time'
import { usePlayer } from '~/hooks/use-player'
import { formatBitrate, formatDuration, formatFileSize } from '~/lib/format'

export const Route = createFileRoute('/_app/tracks/$trackId')({
  component: TrackDetailPage,
})

function TrackDetailPage() {
  const { trackId: trackIdParam } = Route.useParams()
  const trackId = Number(trackIdParam)
  const { data: track, isLoading } = useTrack(trackId)
  const { data: lyricsData } = useTrackLyrics(trackId)
  const { data: similarData } = useSimilarTracks(trackId, 10)
  const player = usePlayer()
  const currentTime = useCurrentTime()

  if (isLoading) return <Skeleton h={400} />
  if (!track) return <Text>Track not found</Text>

  const playTrack = () => {
    player.play([trackToQueueItem(track)])
  }

  const currentTrack = player.state.queue[player.state.currentIndex]
  const isCurrentTrack = currentTrack?.id === track.id

  return (
    <Stack gap="lg">
      <Group align="flex-start" gap="xl">
        <div style={{ width: 250, flexShrink: 0 }}>
          <CoverArt type="tracks" id={track.id} alt={track.title} size={500} w={250} h={250} />
        </div>
        <Stack gap="xs" style={{ flex: 1 }}>
          <Title order={1}>{track.title}</Title>
          <Text
            renderRoot={(props) => (
              <Link
                {...props}
                to="/artists/$artistId"
                params={{ artistId: String(track.artistId) }}
              />
            )}
            size="lg"
            c="dimmed"
            td="none"
          >
            {track.artist}
          </Text>
          <Text
            renderRoot={(props) => (
              <Link {...props} to="/albums/$albumId" params={{ albumId: String(track.albumId) }} />
            )}
            size="md"
            c="dimmed"
            td="none"
          >
            {track.album}
          </Text>
          <Group gap="xs">
            <Text c="dimmed">{formatDuration(track.duration)}</Text>
            <Text c="dimmed">&middot;</Text>
            <Text c="dimmed">{formatBitrate(track.bitRate)}</Text>
            <Text c="dimmed">&middot;</Text>
            <Text c="dimmed">{formatFileSize(track.size)}</Text>
            {track.year && (
              <>
                <Text c="dimmed">&middot;</Text>
                <Text c="dimmed">{track.year}</Text>
              </>
            )}
          </Group>
          <Group mt="sm">
            <Button leftSection={<IconPlayerPlay size={16} />} onClick={playTrack}>
              Play
            </Button>
            <EnqueueButton tracks={[trackToQueueItem(track)]} />
            <FavoriteButton type="track" id={track.id} favorited={track.favorited} />
          </Group>
          {track.keyName && <Badge variant="light">{track.keyName}</Badge>}
        </Stack>
      </Group>

      {track.mood && (
        <Card withBorder>
          <Title order={4} mb="sm">
            Mood Analysis
          </Title>
          <Stack gap="xs">
            {Object.entries(track.mood).map(([key, value]) =>
              value != null ? (
                <Group key={key} gap="xs">
                  <Text size="sm" w={100} tt="capitalize">
                    {key}
                  </Text>
                  <Progress value={value * 100} size="sm" style={{ flex: 1 }} />
                  <Text size="xs" c="dimmed" w={40}>
                    {(value * 100).toFixed(0)}%
                  </Text>
                </Group>
              ) : null
            )}
          </Stack>
        </Card>
      )}

      {lyricsData?.syncedLyrics && lyricsData.syncedLyrics.length > 0 ? (
        <Card withBorder>
          <Title order={4} mb="sm">
            Lyrics
          </Title>
          <SyncedLyrics
            lyrics={lyricsData.syncedLyrics}
            currentTime={isCurrentTrack ? currentTime : 0}
          />
          {lyricsData.source && (
            <Text size="xs" c="dimmed" mt="sm">
              Source: {lyricsData.source}
            </Text>
          )}
        </Card>
      ) : lyricsData?.lyrics ? (
        <Card withBorder>
          <Title order={4} mb="sm">
            Lyrics
          </Title>
          <Text style={{ whiteSpace: 'pre-wrap' }}>{lyricsData.lyrics}</Text>
          {lyricsData.source && (
            <Text size="xs" c="dimmed" mt="sm">
              Source: {lyricsData.source}
            </Text>
          )}
        </Card>
      ) : null}

      {similarData && (
        <section>
          <Title order={3} mb="md">
            Similar Tracks
          </Title>
          {similarData.data.length > 0 ? (
            <TrackTable tracks={similarData.data} />
          ) : (
            <Text c="dimmed" size="sm">
              No Last.fm data available for this track.
            </Text>
          )}
        </section>
      )}
    </Stack>
  )
}
